import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { POLICY_DOCS, DEFAULT_DOC } from "constants/policyContent";
import { callGetPolicy, callGetPolicyVersion } from "api/MuyeonApi";

// 마크다운 유사 텍스트 → JSX (## / ### 제목, - 목록, 표(| a | b |)는 목록 한 줄씩, 그 외 문단). 새 CSS 없이 기존 .privacy-policy 스코프 사용.
//  ⚠️ 관리자 '약관·정책' 미리보기(muyeon-admin policies.js renderMd)와 규칙을 맞출 것.
export function renderMd(md) {
  const out = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={`ul-${out.length}`} style={{ paddingLeft: "1.6rem", listStyle: "disc" }}>
          {list.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      );
      list = [];
    }
  };
  let tableHeader = true;
  md.split("\n").forEach((line, i) => {
    const t = line.trim();
    if (!t) { flushList(); tableHeader = true; return; }
    // ★ 2026-11-03 표 — 새 CSS 없이 '첫 칸 · 나머지 칸' 목록으로. 머리줄·구분줄(|---|)은 뺀다.
    if (t.startsWith("|") && t.endsWith("|")) {
      const cells = t.slice(1, -1).split("|").map((c) => c.trim());
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) return;
      if (tableHeader) { tableHeader = false; return; }
      list.push(cells.filter(Boolean).join(" · "));
      return;
    }
    tableHeader = true;
    if (t.startsWith("### ")) { flushList(); out.push(<h4 key={i} style={{ marginTop: "1.6rem" }}>{t.slice(4)}</h4>); }
    else if (t.startsWith("## ")) { flushList(); out.push(<h3 key={i}>{t.slice(3)}</h3>); }
    else if (t.startsWith("- ")) { list.push(t.slice(2)); }
    else { flushList(); out.push(<p key={i}>{t}</p>); }
  });
  flushList();
  return out;
}

/**
 * 약관·정책 문서 — 서버(관리자 '약관·정책')의 버전 문서를 그린다.
 *  ★ 앱(iOS·안드로이드)도 이 페이지를 연다(`/policy?doc=…&embed=1`) → 앱 재배포 없이 반영.
 *  ★ 서버를 못 부르면 constants/policyContent.js 문구로 폴백한다(약관 화면이 비면 안 된다).
 *  ?version= 이 있으면 그 버전(지난 버전·시행 예정 버전)을 보여 준다 — 개인정보처리방침 제17조의 이전 버전 열람.
 */
export const Policy = () => {
  const [params] = useSearchParams();
  const key = params.get("doc") || DEFAULT_DOC;
  const version = params.get("version") || "";
  const fallback = useMemo(() => POLICY_DOCS[key] || POLICY_DOCS[DEFAULT_DOC], [key]);
  const [doc, setDoc] = useState(null); // 서버 현재 문서(버전 목록·시행 예정 포함)
  const [versionDoc, setVersionDoc] = useState(null); // ?version= 으로 연 특정 버전

  useEffect(() => {
    let alive = true;
    setDoc(null);
    setVersionDoc(null);
    callGetPolicy(key)
      .then((r) => { if (alive && r?.data?.body) setDoc(r.data); })
      .catch(() => {});
    if (version) {
      callGetPolicyVersion(key, version)
        .then((r) => { if (alive && r?.data?.body) setVersionDoc(r.data); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [key, version]);

  // 같은 창(embed 유지)에서 버전을 오간다.
  const linkTo = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("version", v); else next.delete("version");
    return `/policy?${next.toString()}`;
  };

  const showing = versionDoc || doc;
  const body = showing?.body ?? fallback.md;
  const isOther = !!versionDoc && versionDoc.version !== doc?.version;

  return (
    <div id="privacy_policy_detail" className="page">
      <div className="content-wrap">
        <div className="privacy-policy">
          <div className="section">
            {isOther && (
              <p className="t-13-single fc-s" style={{ marginBottom: "1.2rem" }}>
                {versionDoc.status === "SCHEDULED"
                  ? `${versionDoc.effectiveDate}부터 시행되는 개정 버전입니다.`
                  : `${versionDoc.effectiveDate} 시행 버전(지난 버전)입니다.`}{" "}
                <Link to={linkTo("")}>현재 버전 보기</Link>
              </p>
            )}
            {!version && doc?.upcoming && (
              <p className="t-13-single fc-s" style={{ marginBottom: "1.2rem" }}>
                {doc.upcoming.effectiveDate}부터 개정된 내용이 적용됩니다.
                {doc.upcoming.changeSummary ? ` (${doc.upcoming.changeSummary})` : ""}{" "}
                <Link to={linkTo(doc.upcoming.version)}>개정 내용 보기</Link>
              </p>
            )}
            {renderMd(body)}
            {!version && doc?.history?.length > 0 && (
              <>
                <h4 style={{ marginTop: "1.6rem" }}>이전 버전</h4>
                <ul style={{ paddingLeft: "1.6rem", listStyle: "disc" }}>
                  {doc.history.map((h) => (
                    <li key={h.version}><Link to={linkTo(h.version)}>{h.effectiveDate} 시행 버전</Link></li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
